/**
 * Controls all board input, including swaps, and gem feedback when the player hovers over or taps a gem.
 */
export default class InputController extends Phaser.Group {
  constructor(boardObj) {
    super(game);
    this.state = game.state.getCurrentState();

    this.board = boardObj;

    this.booster = null;

    this.clicked = false;
    this.clickedCell = false;

    this.anyWindowOpen = false;

    this.firstTouchMade = false;

    this.possibleCandies = [];


    G.sb('onWindowOpened').add(() => {
      this.anyWindowOpen = true;
    }, this);

    G.sb('onAllWindowsClosed').add(() => {
      this.anyWindowOpen = false;
    }, this);

    if (!this.state.EDITOR) {
      game.input.onDown.add(this.onClick, this);
    }


    game.input.onUp.add(() => {
      this.clicked = false;
    }, this);

    this.locked = false;
  }

  /**
   * Stop all tweens on the selected gem.
   */
  stopTweens() {
    if (this.selectTween) {
      this.selectTween.stop();
      this.popTween.stop();
      this.selectTween.target.set(1);
    }
  }

  /**
   * Runs every frame. Used to give feedback when the player is hovered over a cell.
   *
   * @memberof InputController
   */
  update() {
    this.board.tileShade.visible = false;

    const over = this.pointerToCell2(game.input.activePointer);

    if (game.device.desktop && !G.lvl.goalAchieved && this.board.isCellOnBoard(over[0], over[1])) {
      this.board.tileShade.visible = true;
      this.board.tileShade.x = this.board.cellXToPxIn(over[0]);
      this.board.tileShade.y = this.board.cellYToPxIn(over[1]);
    }


    if (!this.canMakeMove()) return;

    if (this.clicked) {
      // for tutorial purposes
      if (this.possibleCandies.length > 0) {
        if (this.possibleCandies.indexOf(this.board.getCandy(this.clickedCell)) === -1
          || this.possibleCandies.indexOf(this.board.getCandy(over)) === -1) {
          return;
        }
      }

      if (over && this.board.isMoveable(over) && this.areNeighbours(this.clickedCell, over) && this.board.getCandy(over)) {
        this.stopTweens();
        this.board.makeMove(this.board.getCandy(this.clickedCell), this.board.getCandy(over));
        this.clicked = false;
        this.clickedCell = null;
      }
    }
  }

  /**
   * Whether or not we can currently make a swap on the board
   *
   * @returns boolean
   * @memberof InputController
   */
  canMakeMove() {
    if (this.locked) return false;
    if (!this.board.actionManager.noAction) return false;
    if (G.lvl.goalAchieved) return false;
    if (this.anyWindowOpen) return false;
    return true;
  }

  /**
   * When the player clicks or taps a gem.
   * @param {object} pointer The pointer object, can represent a mouse or tap.
   * @memberof InputController
   */
  onClick(pointer) {
    if (!this.canMakeMove()) return;


    const cell = this.pointerToCell(pointer);

    if (!cell || !this.board.isMoveable(cell[0], cell[1])) return;

    if (this.board.getCandy(cell)) {
      const candy = this.board.getCandy(cell);

      // track the first gem interaction on the first level
      if (G.lvl.lvlIndex === 0 && !this.firstTouchMade) {
        this.firstTouchMade = true;
        OMT.platformTracking.logFTUEvent('FTUFirstInteractionWithGems');
      }

      G.sfx.pop.play();
      this.stopTweens();
      this.board.selectShade.visible = false;
      if (this.clickedCell) {
        if (Math.abs(this.clickedCell[0] - cell[0]) + Math.abs(this.clickedCell[1] - cell[1]) === 1) {
          if (this.possibleCandies.length > 0) {
            if (this.possibleCandies.indexOf(this.board.getCandy(this.clickedCell)) > -1
              && this.possibleCandies.indexOf(this.board.getCandy(cell)) > -1) {
              this.board.makeMove(this.board.getCandy(this.clickedCell), this.board.getCandy(cell));
              this.clickedCell = null;
              this.clicked = false;
              return;
            }
          } else {
            this.board.makeMove(this.board.getCandy(this.clickedCell), this.board.getCandy(cell));
            this.clickedCell = null;
            this.clicked = false;
            return;
          }
        }
      }


      // if we havent selected anything, or the thing we're clicking isnt a neighbour of the selected cell, select the cell we clicked.
      // 'isnt a neighbour' in this case means either the cell we're clicking is somewhere else on the board, or we're clicking on the selected cell itself
      if (!this.clickedCell || !this.areNeighbours(this.clickedCell, cell)) {
        this.selectTween = game.add.tween(candy.scale).to({ x: 1.15, y: 1.15 }, 220, null, false, 0, -1, true);
        this.popTween = game.add.tween(candy.scale).to({ x: 1.23, y: 1.23 }, 110, null, true, 0, 0, true);
        this.popTween.chain(this.selectTween);
        //this.board.selectShade.visible = true;
        this.board.selectShade.x = this.board.cellXToPxIn(cell[0]);
        this.board.selectShade.y = this.board.cellYToPxIn(cell[1]);
      }
      this.clicked = true;
      this.clickedCell = cell;
    }
  }

  /**
   * Converts the pointer position to a board grid position.
   *
   * @param {*} pointer The pointer object, can represent a mouse or tap.
   * @returns If the pointer is inside the board, an array containing the x and y position of the grid cell. Otherwise false.
   * @memberof InputController
   */
  pointerToCell(pointer) {
    if (this.anyWindowOpen) return false;

    const xx = pointer.worldX;
    const yy = pointer.worldY;

    if (this.isPointerInRange(pointer)) {
      return [
        Math.floor((xx - (this.board.x + this.board.offsetX)) / (this.board.tilesize * this.board.scale.x)),
        Math.floor((yy - (this.board.y + this.board.offsetY)) / (this.board.tilesize * this.board.scale.y)),
      ];
    }
    return false;
  }

  /**
   * Converts the pointer position to a board grid position.
   * @param {object} pointer The pointer object, can represent a mouse or tap
   * @returns An array containing the x and y positions of the grid cell.
   * @memberof InputController
   */
  pointerToCell2(pointer) {
    const xx = pointer.worldX;
    const yy = pointer.worldY;

    return [
      Math.floor((xx - (this.board.x + this.board.offsetX)) / (this.board.tilesize * this.board.scale.x)),
      Math.floor((yy - (this.board.y + this.board.offsetY)) / (this.board.tilesize * this.board.scale.y)),
    ];
  }

  /**
   * Determines whether the pointer is on the board.
   * @param {object} pointer The pointer object. Can represent a mouse or tap.
   * @memberof InputController
   */
  isPointerInRange(pointer) {
    const x = pointer.worldX;
    const y = pointer.worldY;

    return !(x < this.board.x + this.board.offsetX || x > this.board.x + this.board.offsetX + this.board.width
      || y < this.board.y + this.board.offsetY || y > this.board.y + this.board.offsetY + this.board.height);
  }

  /**
   * Determines whether cell1 and cell2 are neighbours. Only checks up/down/left/right, no diagonals.
   * @param {Array} cell1 Cell 1.
   * @param {Array} cell2 Cell 2.
   * @memberof InputController
   */

  areNeighbours(cell1, cell2) {
    if (cell1[0] === cell2[0]) {
      return Math.abs(cell1[1] - cell2[1]) === 1;
    }

    if (cell1[1] === cell2[1]) {
      return Math.abs(cell1[0] - cell2[0]) === 1;
    }
    return false;
  }

  /**
   * Determines whether the cells are identical, by comparing their x and y values.
   *
   * @param {*} cell1 Cell 1.
   * @param {*} cell2 Cell 2.
   * @returns Whether or not the cells are identical.
   * @memberof InputController
   */
  compareCells(cell1, cell2) {
    return cell1.x === cell2.x && cell1.y === cell2.y;
  }
}

G.InputController = InputController;
